export interface IApnsConfiguration {
    is_active: boolean
    expiration?: string | null
    push_package: {
        website_push_id: string
        icon_source_url: string
    }
    private_key: {
        private_key_file_url: string
        private_key_password?: string
    }
}

export enum SendIntegrationChannel {
    WEB = 'WEB',
    NATIVE = 'NATIVE',
}

export enum SendIntegrationProvider {
    VAPID = 'VAPID',
    APNS = 'APNS',
    FCM = 'FCM',
}

export enum SendIntegrationPrivateKeyType {
    P8 = 'P8',
    P12 = 'P12',
}

export enum FcmSendIntegrationApiVersion {
    LEGACY = 'LEGACY',
    V1 = 'V1',
}

export interface IAbstractSendIntegration {
    is_active: boolean
}

// Possible Combinations

// WEB VAPID - combination of domains.vapid_public_key + domains.vapid_private_key
export interface IWebVapidSendIntegration extends IAbstractSendIntegration {
    is_active: true // not currently able to disable this integration
    public_key: string
    private_key: string
}

// WEB APNS - existing domains.apns_configuration_json JSONB
export interface IWebApnsSendIntegration extends IAbstractSendIntegration {
    readonly expiration?: string // ISO8601 Date String
    private_key?: {
        private_key_file_url: string
        private_key_password?: string
    }
    push_package?: {
        website_push_id: string
        icon_source_url: string
    }
}

type RequireKeys<T, Key extends keyof T> = T & { [K in Key]-?: T[K] }

// NATIVE APNS - domains.native_apns_configuration JSONB
export interface IAbstractNativeApnsSendIntegration extends IAbstractSendIntegration {
    app_bundle_id?: string
    private_key_file_url?: string
    private_key_type?: SendIntegrationPrivateKeyType
    // P12 Only
    private_key_pass_phrase?: string
    expiration?: string
    // P8 Only
    team_id?: string
    key_id?: string
}

export type P12NativeApnsSendIntegration = {
    is_active: true
    private_key_type: SendIntegrationPrivateKeyType.P12
} & Omit<IAbstractNativeApnsSendIntegration, 'team_id' | 'key_id'>

export type P8NativeApnsSendIntegration = {
    is_active: true
    private_key_type: SendIntegrationPrivateKeyType.P8
} & RequireKeys<
    Omit<IAbstractNativeApnsSendIntegration, 'app_bundle_id' | 'private_key_pass_phrase'>,
    'team_id' | 'key_id'
>

// NATIVE FCM - domains.native_fcm_configuration JSONB
export interface IAbstractNativeFcmSendIntegration extends IAbstractSendIntegration {
    api_version?: FcmSendIntegrationApiVersion
    sender_id?: string
    // LEGACY Only
    server_api_key?: string
    // V1 Only
    service_account_json_file_url?: string
}

export type NativeLegacyFcmSendIntegration = {
    is_active: true
    api_version: FcmSendIntegrationApiVersion.LEGACY
} & RequireKeys<Omit<IAbstractNativeFcmSendIntegration, 'service_account_json_file_url'>, 'server_api_key'>

export type NativeV1FcmSendIntegration = {
    is_active: true
    api_version: FcmSendIntegrationApiVersion.V1
} & RequireKeys<Omit<IAbstractNativeFcmSendIntegration, 'server_api_key'>, 'service_account_json_file_url'>

export interface IPushPackageConfiguration {
    website_push_id?: string
    icon_source_url?: string
}

export interface IAbstractApnsPrivateKeyConfiguration {
    private_key_file_url?: string
    private_key_pass_phrase?: string
}
